import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SwipeableViews from 'react-swipeable-views'
import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useTheme } from '@mui/material/styles'
import { isEqual } from 'lodash'
import { useVenti } from 'venti'
import { Tabs, Tab, Card, CardContent } from '@mui/material'

import Page from '../../../components/Page'
import { LoadingBtn } from '../../../components/Button'
import { ModalYesNo } from '../../../components/modals/ModalYesNo'
import { ModalError } from '../../../components/modals/ModalError'
import { useAppContext } from '../../../components/AppContext'
import { Loading } from '../../../components/Loading'
import Features from '../../../components/admin/Features'
import { WebsiteSettings } from '../../../components/admin/WebsiteSettings'
import { Integrations } from '../../../components/admin/Integrations'
import TabPanel from '../../../components/TabPanel'
import ProfileView from './Profile'

import { navigationLinking } from '../../../services/navigation'
import {
	siteConfigurationModelWithNulls,
	validateURL,
} from '../../../services/utils'
import {
	a11yProps,
	getErrorMessage,
	isValidNMLSId,
	validateSiteConfigurationData,
} from '../../../services/helper'
import { eventTypes } from '../../../services/constants'
import queryKeys from '../../../services/queryKeys'

import { useAlert, useWindowSize } from '../../../hooks'
import { useMixpanel } from '../../../hooks/useMixpanel'
import { getTheme } from '../../../config'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import MobileSettings from '../../../components/admin/MobileSettings/MobileSettings'

const theme = getTheme()

export default function AdminBranchEdit({ navigation }) {
	const { alert } = useAlert()
	const { id } = useParams()
	const { state } = useAppContext()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const themeMUI = useTheme()
	const { siteConfig } = state
	const ventiState = useVenti()
	const [width] = useWindowSize()
	const mixpanel = useMixpanel()

	const isCreateNewBranch = id === 'new'
	const siteConfigurationId = ventiState.get(
		theme.storageKeys.editingBranchSiteConfigurationId
	)
	const [activeTab, setActiveTab] = useState(0)
	const [modalConfirmation, setModalConfirmation] = useState({
		open: false,
		text: '',
		leavePage: false,
		canBeSaved: false,
		isSave: false,
		event: null,
	})
	const [initialState, setInitialState] = useState({
		...siteConfigurationModelWithNulls,
		updateDownstream: false,
		domain: '',
		entityType: 2,
	})
	const [formData, setFormData] = useState({
		...siteConfigurationModelWithNulls,
		updateDownstream: false,
		domain: '',
		entityType: 2,
	})
	const [inheritedSiteConfig, setInheritedSiteConfig] = useState({})
	const [canBeSubmitted, setCanBeSubmitted] = useState(true)
	const [errorModalVisible, setErrorModalVisible] = useState(false)
	const [errorMsg, setErrorMsg] = useState('')
	const initialRender = useRef(true)
	const [branch, setBranch] = useState(null)

	const {
		error: errorFetchingBranch,
		data: branchResult,
		refetch,
	} = useQuery({
		queryKey: [queryKeys.branch, id],
		queryFn: () => TheBigPOSApi.getBranch(id),
		enabled: !!id && !isCreateNewBranch,
		retry: 2,
		refetchOnWindowFocus: false,
	})

	const {
		error: errorFetchingBranchSiteConfiguration,
		data: branchSiteConfigurationResult,
		refetch: refetchBranchSiteConfiguration,
	} = useQuery({
		queryKey: [
			queryKeys.branchSiteConfiguration,
			siteConfigurationId,
		],
		queryFn: () =>
			TheBigPOSApi.getBranchSiteConfiguration(
				id,
				siteConfigurationId
			),
		enabled: !!id && !isCreateNewBranch && !!siteConfigurationId,
		retry: 2,
	})

	useEffect(() => {
		if (!id) navigate(`/${navigationLinking.AdminBranch}`)
	}, [id])

	useEffect(() => {
		if (errorFetchingBranch) {
			alert('There was a problem loading the branch', {
				severity: 'error',
			})
		}
	}, [errorFetchingBranch])

	useEffect(() => {
		if (errorFetchingBranchSiteConfiguration) {
			alert(
				'There was a problem loading the branch site configuration',
				{
					severity: 'error',
				}
			)
		}
	}, [errorFetchingBranchSiteConfiguration])

	useEffect(() => {
		const formCopy = { ...formData }
		delete formCopy.domain

		const canBeSubmitted =
			formData?.url?.trim() !== '' &&
			formData?.licenses?.length > 0 &&
			formData?.name?.length > 0 &&
			isValidNMLSId(formData?.nmlsid) &&
			!isEqual(initialState, formCopy)
		setCanBeSubmitted(canBeSubmitted)

		if (initialRender.current) {
			initialRender.current = false
		}
	}, [formData, initialState])

	useEffect(() => {
		if (branchResult?.data && branchSiteConfigurationResult?.data) {
			setFormData({
				...branchSiteConfigurationResult.data.siteConfiguration,
				corporateId: branchResult.data.corporateID,
				type: branchResult.data.type,
				domain: '',
			})
			setInheritedSiteConfig(
				branchSiteConfigurationResult.data.inheritedSiteConfiguration
			)
			setInitialState(
				branchSiteConfigurationResult.data.siteConfiguration
			)
		}
	}, [branchResult, branchSiteConfigurationResult])

	const invalidateQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [queryKeys.branches, queryKeys.branch],
		})
		await refetch()
	}

	const invalidateSiteConfigurationQueries = async () => {
		await queryClient.invalidateQueries({
			queryKey: [
				queryKeys.branchSiteConfiguration,
				siteConfigurationId,
			],
		})
		await refetchBranchSiteConfiguration()
	}

	const createBranchMutation = useMutation({
		mutationFn: (data) => TheBigPOSApi.createBranch(data),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [queryKeys.branches],
			})
		},
	})

	const createBranchSiteConfigurationMutation = useMutation({
		mutationFn: ({ formData, branchId }) =>
			TheBigPOSApi.createBranchSiteConfiguration(branchId, formData),
	})

	const updateBranchSiteConfigurationMutation = useMutation({
		mutationFn: ({ formData, branchId, siteConfigurationId }) =>
			TheBigPOSApi.replaceBranchSiteConfiguration(
				branchId,
				siteConfigurationId,
				formData
			),
		onSuccess: invalidateSiteConfigurationQueries,
	})

	const updateBranchMutation = useMutation({
		mutationFn: (data) => TheBigPOSApi.replaceBranch(data.id, data),
		onSuccess: invalidateQueries,
	})

	const createSiteConfiguration = async (branch, name) => {
		await createBranchSiteConfigurationMutation.mutateAsync({
			formData,
			branchId: branch.data.id,
		})

		mixpanel.trackEvent(eventTypes.BRANCH_CREATED, {
			name,
			id: branch.data.id,
		})
		alert(`Branch "${name}" successfully created`)
		navigate(`/${navigationLinking.AdminBranch}`)
	}

	const saveForm = async () => {
		try {
			const {
				id: formId,
				name,
				corporateId,
				type,
				url,
				domain,
				branchId,
			} = formData

			const error = validateURL(url, domain)
			if (error) {
				setErrorMsg(error)
				setErrorModalVisible(true)
				return
			}

			if (formId) {
				await updateBranchMutation.mutateAsync({
					id,
					name: name,
					corporateID: corporateId,
					type,
				})
				await updateBranchSiteConfigurationMutation.mutateAsync({
					formData,
					branchId: id,
					siteConfigurationId: formId,
				})

				mixpanel.trackEvent(eventTypes.BRANCH_UPDATED, {
					name,
					id: formId,
				})
				alert(`Branch "${name}" successfully updated`)
				navigate(`/${navigationLinking.AdminBranch}`)
			} else {
				validateSiteConfigurationData(formData)
				if (!createBranchSiteConfigurationMutation.error) {
					const branch = await createBranchMutation.mutateAsync({
						name,
						corporateID: corporateId,
						type,
					})
					setBranch(branch)
					await createSiteConfiguration(branch, name)
				} else {
					await createSiteConfiguration(branch, name)
				}
			}
		} catch (e) {
			alert(getErrorMessage(e), { severity: 'error' })
		}
	}

	const handleCheckFormLicenses = () => {
		formData.licenses.length === 0
			? setModalConfirmation({
					open: true,
					text: theme.modal.licensesText,
					leavePage: false,
					canBeSaved: false,
					isSave: true,
					event: null,
				})
			: saveForm()
	}

	const modalConfirm = async (modalConfirmation) => {
		modalConfirmation.leavePage
			? navigation.dispatch(modalConfirmation.event.data.action)
			: setModalConfirmation({
					open: false,
					text: '',
					leavePage: false,
				})
	}

	const handleTabChange = (event, newValue) => {
		if (newValue !== 0 && !formData.corporateId) {
			setErrorMsg('Please select a corporation.')
			setErrorModalVisible(true)
			return
		}
		setActiveTab(newValue)
	}

	const handleChangeIndex = (index) => {
		setActiveTab(index)
	}

	const handleFeaturesChange = (data) => {
		setFormData((current) => ({
			...current,
			...data,
		}))
	}

	return (
		<Page
			isFullWidth={true}
			title={isCreateNewBranch ? `New Branch` : 'Edit Branch'}
		>
			<ModalYesNo
				modalConfirmation={modalConfirmation}
				modalConfirm={modalConfirm}
				setModalConfirmation={setModalConfirmation}
			/>
			<ModalError
				visible={errorModalVisible}
				setVisible={setErrorModalVisible}
				message={errorMsg}
			/>
			<div className="pl-5 pr-5 pb-20 pt-5 h-screen overflow-auto">
				<p className="text-xl md:text-2xl font-rubik font-bold mr-4 mb-10 dark:text-white">
					{isCreateNewBranch ? `New Branch` : 'Edit Branch'}
				</p>
				{!isCreateNewBranch && !initialState?.id ? (
					<div
						className="flex justify-center dark:bg-[#121212]"
						style={{ height: `calc(100vh - 216px)` }}
					>
						<Loading size="small" />
					</div>
				) : (
					<>
						<div className="flex flex-wrap justify-end my-5">
							{/*{formData?.id && (*/}
							{/*	<FormControlLabel*/}
							{/*		label={*/}
							{/*			<Typography className="dark:text-white">*/}
							{/*				Apply to All Child Sites*/}
							{/*			</Typography>*/}
							{/*		}*/}
							{/*		key="AdminBranchEditApplyToChildrenButton"*/}
							{/*		control={*/}
							{/*			<Checkbox*/}
							{/*				id="AdminBranchEditApplyToChildrenButton"*/}
							{/*				checked={formData?.updateDownstream || false}*/}
							{/*				onChange={() =>*/}
							{/*					setFormData((current) => ({*/}
							{/*						...current,*/}
							{/*						updateDownstream: !current.updateDownstream,*/}
							{/*					}))*/}
							{/*				}*/}
							{/*				inputProps={{ 'aria-label': 'controlled' }}*/}
							{/*			/>*/}
							{/*		}*/}
							{/*	/>*/}
							{/*)}*/}
							<LoadingBtn
								id={`AdminBranchEditSaveButton`}
								disabled={!canBeSubmitted}
								loading={
									updateBranchMutation.isPending ||
									createBranchMutation.isPending
								}
								text={`${formData?.id ? 'Update Branch' : 'Create Branch'}`}
								onClick={handleCheckFormLicenses}
								fullWidth={false}
							/>
						</div>
						<Tabs
							variant="scrollable"
							scrollButtons="auto"
							value={activeTab}
							onChange={handleTabChange}
							aria-label="Tabs"
						>
							<Tab label="Branch Profile" {...a11yProps(0)} />
							<Tab label="Branding & Images" {...a11yProps(1)} />
							<Tab label="Features" {...a11yProps(2)} />
							<Tab label="Integrations" {...a11yProps(3)} />
							<Tab label="Mobile Settings" {...a11yProps(4)} />
						</Tabs>
						<SwipeableViews
							axis={themeMUI.direction === 'rtl' ? 'x-reverse' : 'x'}
							index={activeTab}
							onChangeIndex={handleChangeIndex}
						>
							<TabPanel
								value={activeTab}
								index={0}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<ProfileView
											siteConfig={siteConfig}
											formData={formData}
											inheritedSiteConfiguration={inheritedSiteConfig}
											setFormData={setFormData}
											setInheritedSiteConfiguration={
												setInheritedSiteConfig
											}
											width={width}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={1}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<WebsiteSettings
											formData={formData}
											setFormData={setFormData}
											inheritedSiteConfiguration={inheritedSiteConfig}
											entityType={2}
											width={width}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={2}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<Features
											entityType="Branch"
											onChange={handleFeaturesChange}
											siteConfiguration={formData}
											inheritedSiteConfiguration={inheritedSiteConfig}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={3}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<Integrations
											formData={formData}
											setFormData={setFormData}
											inheritedSiteConfiguration={inheritedSiteConfig}
											entityType={2}
											width={width}
										/>
									</CardContent>
								</Card>
							</TabPanel>
							<TabPanel
								value={activeTab}
								index={4}
								dir={themeMUI.direction}
							>
								<Card>
									<CardContent>
										<MobileSettings entityType="Branch" />
									</CardContent>
								</Card>
							</TabPanel>
						</SwipeableViews>
					</>
				)}
			</div>
		</Page>
	)
}
